import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { UnderlineTabs } from '@/components/common/UnderlineTabs';
import type { NewsContentType } from '@/types/newsService';

interface FilterTabsProps {
  activeContentType: NewsContentType | null;
  onContentTypeChange: (type: NewsContentType | null) => void;
  availableContentTypes: NewsContentType[];
}

/** Fallback labels for content types with no translation yet. */
const CONTENT_TYPE_LABELS: Record<string, string> = {
  news: 'News',
  analysis: 'Analysis',
  opinion: 'Opinion',
  guide: 'Guide',
  recap: 'Recap',
  interview: 'Interview',
};

/**
 * Le sottosezioni del feed (Recenti / Notizie / Guida…).
 *
 * Schede sottolineate e non pastiglie: su alcuni giochi (FC26) questa fila è
 * piena di sottosezioni, e con le pastiglie ogni voce porterebbe una superficie
 * finché la riga diventa un muro. La grafica sta in `UnderlineTabs`, condivisa
 * col selettore delle finestre nel Trend meta.
 *
 * `null` è "tutte" e viaggia come stringa vuota: `UnderlineTabs` usa il valore
 * come chiave di React, e `null` non è una chiave.
 */
const ALL = '';

export const FilterTabs = memo(function FilterTabs({
  activeContentType,
  onContentTypeChange,
  availableContentTypes,
}: FilterTabsProps) {
  const { t } = useTranslation('news');

  const options = useMemo(
    () => [
      { value: ALL, label: t('filter.all', { defaultValue: 'Latest' }) },
      ...availableContentTypes.map((ct) => ({
        value: ct as string,
        label: t(`contentType.${ct}`, { defaultValue: CONTENT_TYPE_LABELS[ct] ?? ct }),
      })),
    ],
    [availableContentTypes, t],
  );

  return (
    <UnderlineTabs
      value={activeContentType ?? ALL}
      onChange={(v) => onContentTypeChange(v === ALL ? null : (v as NewsContentType))}
      options={options}
    />
  );
});
